import { ref } from 'vue'
import { defineStore } from 'pinia'
import { getProductSettings } from '@/apis'
import {
  getAccountIdFromUrlOrDom,
  getDefaultGlobalSettings,
  log,
} from '@/utils'
import type {
  ISettings,
  ISettingsStore,
  IApiProductSettingsResponse,
  IPage,
  IGlobalSettings,
} from '@/types'

export const useSettingsStore = defineStore('settings', (): ISettingsStore => {
  // State
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const initialized = ref(false)
  const accountId = getAccountIdFromUrlOrDom()
  const productId = 'lightning_model_page'
  const settings = ref<ISettings | null>(null)
  const globalSettings = ref<IGlobalSettings | null>(null)
  const pageSettings = ref<IPage | null>(null)

  log('info', 'accountId', accountId)

  // Helpers
  const validateSettings = (newSettings: ISettings): boolean => {
    return (
      newSettings?.pages !== undefined &&
      newSettings?.globalSettings !== undefined
    )
  }

  const parseMakeModelAndYear = () => {
    if (window.location.hash.length) {
      const hashParams = window.location.hash.substring(1).split('/')
      return {
        year: decodeURIComponent(hashParams[0]),
        make: decodeURIComponent(hashParams[1]),
        model: decodeURIComponent(hashParams[2]),
      }
    }

    const siteUrl = window.location.href
    const urlParams = (siteUrl.split('models/')?.[1] || '').split('/')

    return {
      year: decodeURIComponent(urlParams[0]),
      make: decodeURIComponent(urlParams[1]),
      model: decodeURIComponent(urlParams[2]),
    }
  }

  const findMatchingModelFromPages = (pages: IPage[]): IPage | null => {
    const { year, make, model } = parseMakeModelAndYear()

    // Early return if essential values are missing
    if (!year || !make || !model) return null

    return (
      pages.find(page => {
        const yearMatched = Number(page.year) === Number(year)
        const makeMatched = page.make.toLowerCase() === make.toLowerCase()
        const modelMatched = page.model.toLowerCase() === model.toLowerCase()

        return yearMatched && makeMatched && modelMatched
      }) || null
    )
  }

  // State mutators
  const setPageSettings = (newPageSettings: IPage) => {
    pageSettings.value = { ...newPageSettings }
  }

  const setGlobalSettings = (newGlobalSettings: IGlobalSettings) => {
    globalSettings.value = { ...newGlobalSettings }
  }

  const updateSettings = (newSettings: ISettings | null) => {
    if (!newSettings || !validateSettings(newSettings)) {
      log(
        'error',
        'Update settings failed - Invalid settings format',
        newSettings,
      )
      return
    }

    settings.value = { ...newSettings }
  }

  const resetError = () => {
    error.value = null
  }

  // API interactions
  const fetchSettings = async () => {
    if (initialized.value) return

    loading.value = true
    error.value = null

    try {
      const productSettings: IApiProductSettingsResponse =
        await getProductSettings(accountId, productId)

      if (!productSettings.success) {
        log('error', 'Fetch product settings failed', productSettings)
        return
      }

      log('log', 'Fetch product settings', productSettings)

      // Handle missing or empty settings
      if (productSettings.data === null) {
        error.value = new Error('No settings found for this account.')
        log('log', 'No settings found for this account.')
        return
      }

      // Ensure we have valid settings structure
      const newSettings = {
        ...productSettings.data,
        globalSettings:
          productSettings.data?.globalSettings || getDefaultGlobalSettings(),
        pages: productSettings.data?.pages || [],
      }

      updateSettings(newSettings)

      // Find matching page for current model
      if (newSettings.pages.length) {
        const matchedPage = findMatchingModelFromPages(newSettings.pages)

        if (matchedPage && matchedPage.status) {
          setPageSettings(matchedPage)
          setGlobalSettings(newSettings.globalSettings)
        } else {
          error.value = new Error('No page found for this model.')
          log('log', 'No page found for this model.')
        }
      }

      log('info', 'Settings loaded', settings.value)
      initialized.value = true
    } catch (err) {
      error.value =
        err instanceof Error ? err : new Error('Unknown error occurred')
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    accountId,
    settings,
    pageSettings,
    globalSettings,
    loading,
    error,
    initialized,

    // Actions
    fetchSettings,
    resetError,
  }
})
